/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, useDisclosure } from '@chakra-ui/core'
import isSolid from 'is-solid'
import { getSearchPost, handleGetPosts, setSelectedPost, setIsPersonalView } from 'redux/actions/feed'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom'
import { getPostsIdsFromLocalStorage } from 'components/Post/utils'
import { wasBrowserRefreshed } from 'utils'
import { Post } from 'redux/reducers/feed'
import { NewsFeedView } from 'components/NewsFeed/NewsFeedView'
import PersonalView from 'components/NewsFeed/PersonalView/PersonalView'
import { PostFilters } from 'components/NewsFeed/PostFilters'
import { PersonalViewFiltersState } from 'components/NewsFeed/PersonalViewFilters'
import { isEmpty } from 'lodash'
import { useSetup } from 'contexts/SetupContext'
import { STEPS, isSetupAllowedForUser } from 'utils/setupStorage'

const NewsFeed = memo(() => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const retailConfig = useSelector((state: RootState) => state.config.retailConfig)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const [sortedPostsIds, setSortedPostsIds] = useState<string[] | null>(null)
  const isPersonalViewRdx = useSelector((state: RootState) => state.feed.isPersonalView)
  const [personalFilters, setPersonalFilters] = useState<PersonalViewFiltersState>({
    contentType: 'all',
    status: 'all',
  })

  useEffect(() => {
    if (isPersonalViewRdx === null && retailConfig) {
      dispatch(setIsPersonalView(retailConfig.homepage === 'Feed' ? false : true))
    }
  }, [isPersonalViewRdx, retailConfig, dispatch])

  const isPersonalView = isPersonalViewRdx ?? (retailConfig?.homepage === 'Feed' ? false : true)
  const { isOpen: isOpenSearchModal, onOpen: onOpenSearchModal, onClose: onCloseSearchModal } = useDisclosure()

  const fetching = useSelector((state: RootState) => state.feed.fetching)
  const posts = useSelector((state: RootState) => state.feed.posts)

  const hasMorePosts = useSelector((state: RootState) => state.feed.hasMorePosts)
  const isShowAllMyPosts = useSelector((state: RootState) => state.feed.isShowAllMyPosts)
  const consolidationFilterOptions = useSelector((state: RootState) => state.feed.consolidationFilterOptions)
  const isConfigReady = useSelector((state: RootState) => state.config.isConfigReady)
  const user_groups = useSelector((state: RootState) => state.config.config.user_groups)

  const isSearchPage = useMemo(() => location.pathname === keys.ROUTE_NAMES.NEWS_FEED_SEARCH, [location.pathname])
  const isMainPage = useMemo(() => location.pathname === keys.ROUTE_NAMES.NEWS_FEED, [location.pathname])

  const { isStepCompleted } = useSetup()

  const isCurrentStepCompleted =
    (isStepCompleted(STEPS.FEED) && !isPersonalViewRdx) || (isStepCompleted(STEPS.AI_FEED) && isPersonalViewRdx)

  const blockContent = !isCurrentStepCompleted && isSetupAllowedForUser(uid)

  useEffect(() => {
    if (isSearchPage) {
      const searchQuery = searchParams.get('query')
      setSearch(searchQuery || '')
      return () => {
        setSearch('')
      }
    }
  }, [isSearchPage])

  useEffect(() => {
    if (isSearchPage) {
      const oldParams = Object.fromEntries(searchParams)
      setSearchParams({ ...oldParams, query: search })
    }
  }, [search])

  const openSearchModalHandler = useCallback(() => {
    if (isMainPage) {
      onOpenSearchModal()
    }
  }, [isMainPage, onOpenSearchModal])

  useEffect(() => {
    if (!isConfigReady) return
    if (retailConfig?.homepage === 'Feed') {
      setIsPersonalView(false)
    } else {
      setIsPersonalView(true)
    }
  }, [retailConfig?.homepage])

  const fetchTasksAnsPosts = useCallback(() => {
    if (!isConfigReady || user_groups === null) return
    const currentEpochTimeUTC = Math.floor(new Date().getTime() / 1000).toString()
    const pinedPostsObject = getPostsIdsFromLocalStorage()
    const handleGetPostsParams = {
      ts: currentEpochTimeUTC,
      is_init: true,
      isRefresh: true,
      ...(pinedPostsObject[uid!] ? { postIds: pinedPostsObject[uid!] } : {}),
    }
    dispatch(handleGetPosts(handleGetPostsParams))
  }, [uid, dispatch, isConfigReady])

  useEffect(() => {
    if (!isConfigReady) return
    if (!isSolid(posts)) sessionStorage.setItem('wasRefreshed', 'true')

    if (isSearchPage && navigationType !== 'POP') {
      const created_by = searchParams.get('created_by') || ''
      const from_ts = searchParams.get('from_ts')
      const to_ts = searchParams.get('to_ts')
      const media = searchParams.get('media')
      dispatch(
        getSearchPost({
          created_by,
          date: { ...(from_ts ? { from_ts: +from_ts } : {}), ...(to_ts ? { to_ts: +to_ts } : {}) },
          is_contain_media: !!media,
          search: searchParams.get('query') || '',
        })
      )
    }
    if (!isSearchPage && wasBrowserRefreshed()) {
      fetchTasksAnsPosts()
    }

    return () => {
      dispatch(setSelectedPost(''))
    }
  }, [isSearchPage, isConfigReady, fetchTasksAnsPosts])

  const handleChangeSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const filteredPostsIds = useMemo(() => {
    if (isShowAllMyPosts && !isPersonalView) return null
    return posts
      .filter((post: Post) => {
        if (!uid || isEmpty(post.viewed)) return false
        const userCanViewPost = Object.keys(post.viewed).includes(uid) && post.created_by !== uid
        const isPostViewed = post.viewed?.[uid] || false
        return userCanViewPost && !isPostViewed
      })
      .map((post: Post) => post.post_id)
  }, [isShowAllMyPosts, posts, uid, isPersonalView])

  useEffect(() => {
    setSortedPostsIds(filteredPostsIds)
  }, [filteredPostsIds])

  const filterParams = useMemo(() => {
    if (!consolidationFilterOptions) return {}

    const hasAll = Object.values(consolidationFilterOptions).some((value) => value.includes('all'))
    if (hasAll) return {}

    const params: Record<string, string[]> = {}
    if (consolidationFilterOptions.groups) {
      params.groups = consolidationFilterOptions.groups
    }
    if (consolidationFilterOptions.tags) {
      params.tags = consolidationFilterOptions.tags
    }
    return params
  }, [consolidationFilterOptions])

  const fetchPosts = useCallback(async () => {
    if (fetching || !isSolid(posts) || !hasMorePosts || !uid) return

    sessionStorage.setItem('wasRefreshed', 'true')
    const lastPostItem = posts.reduceRight((a: any) => a)
    const { created_at_ts } = lastPostItem
    const pinedPostsObject = getPostsIdsFromLocalStorage()

    const handleGetPostsParams = {
      ts: created_at_ts.toString(),
      ...(pinedPostsObject[uid] ? { postIds: pinedPostsObject[uid] } : {}),
      ...filterParams,
    }

    try {
      const fetchedPosts: any = await dispatch(handleGetPosts(handleGetPostsParams))
      if (!isShowAllMyPosts) {
        setSortedPostsIds((currentSortedPostsIds) => [
          ...(currentSortedPostsIds || []),
          ...fetchedPosts.map((post: Post) => post.post_id),
        ])
      }
    } catch (err) {
      console.log(err)
    }
  }, [dispatch, fetching, hasMorePosts, posts, uid, filterParams, isShowAllMyPosts])

  return (
    <Box
      w="100%"
      style={{
        filter: blockContent ? 'blur(10px)' : 'blur(0px)',
        transition: 'filter 0.8s ease-out',
      }}
      pointerEvents={blockContent ? 'none' : 'auto'}
    >
      <Flex
        flexDir="row"
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        mx="auto"
        minW="600px"
        maxW="100%"
        style={{ columnGap: '50px' }}
      >
        {!isSearchPage && user_groups !== null && consolidationFilterOptions && (
          <PostFilters
            uid={uid!}
            user_groups={user_groups}
            consolidationFilterOptions={consolidationFilterOptions}
            isShowAllMyPosts={isShowAllMyPosts}
            openSearchModalHandler={openSearchModalHandler}
            isDisabled={fetching || blockContent}
            isPersonalView={isPersonalView}
            onViewChange={(value: boolean) => dispatch(setIsPersonalView(value))}
            onPersonalFiltersChange={setPersonalFilters}
          />
        )}
      </Flex>
      <Box w="100%">
        {isPersonalView ? (
          <PersonalView sortedPostsIds={sortedPostsIds || []} filters={personalFilters} />
        ) : (
          <NewsFeedView
            search={search}
            sortedPostsIds={sortedPostsIds}
            onSearchChange={handleChangeSearch}
            openSearchModalHandler={openSearchModalHandler}
            fetchPosts={fetchPosts}
            isOpenSearchModal={isOpenSearchModal}
            onCloseSearchModal={onCloseSearchModal}
          />
        )}
      </Box>
    </Box>
  )
})

export default NewsFeed
